import { DestroyRef, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject } from 'rxjs';
import { CONFIG } from 'src/app/shared/models/config';
import { SessionWarningDialogComponent } from '../components/session-warning/session-warning-dialog.component';
import { AnvandareOrganisation, Session } from './auth.model';
import { AuthService } from './auth.service';
import { getToken } from 'src/app/shared/helpers/auth-utils/auth.utils';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  public session = new BehaviorSubject<Session | null>(null);
  private timeout!: any;
  private warningTimeout!: any;
  private idleWarningTime = CONFIG.idleWarningTime;
  private sessionTimeout = CONFIG.sessionTimeout;
  private _isInternUser!: boolean;
  public userName = signal('');
  private warningShown = false;
  private timeoutDialog: DynamicDialogRef | null = null;
  private initiated = false;
  private _loggedInSingleOrg = new BehaviorSubject<boolean>(false);
  public loggedInSingleOrg$ = this._loggedInSingleOrg.asObservable();
  public anvandarOrganisationer = signal<AnvandareOrganisation[]>([]);

  constructor(
    private authService: AuthService,
    private dialog: DialogService,
    private destroy: DestroyRef,
  ) {
    this.setToken();
  }

  public setToken(refreshToken?: any) {
    if (refreshToken) {
      this.authService.saveSession(refreshToken);
      this.setUserType(refreshToken);
      this.session.next(getToken(refreshToken.access_token));
    } else {
      const token = sessionStorage.getItem('token');
      if (token && JSON.parse(token).access_token !== 'token') {
        const session = JSON.parse(token);
        this.setUserType(session);
        this.session.next(getToken(session.access_token));
      }
    }
    this.session.pipe(takeUntilDestroyed(this.destroy)).subscribe(() => {
      this.userLoggedInToSingleOrg();
    });

    this.authService.loggedIn$.pipe(takeUntilDestroyed(this.destroy)).subscribe((loggedIn) => {
      if (loggedIn && !this.initiated) {
        this.initSessionTimeout();
      } else if (!loggedIn) {
        this.cleanupSession();
      }
    });
  }

  public logout() {
    this.session.next(null);
  }

  private cleanupSession() {
    this.initiated = false;
    this.clearSessionTimers();
    // Remove activity listeners
    ['click', 'mousemove', 'keypress', 'scroll'].forEach((event) => {
      window.removeEventListener(event, this.updateActivity);
    });
  }

  public initSessionTimeout() {
    this.initiated = true;
    this.resetTimeout();
    ['click', 'mousemove', 'keypress', 'scroll'].forEach((event) => {
      window.addEventListener(event, () => this.updateActivity());
    });
  }

  private updateActivity() {
    if (!this.warningShown) {
      this.resetTimeout();
    }
  }

  private resetTimeout() {
    clearTimeout(this.timeout);
    clearTimeout(this.warningTimeout);

    // Set the warning timeout only if the warning has not been shown yet
    if (!this.warningShown && this.authService.loggedIn) {
      this.warningTimeout = setTimeout(() => this.showWarningMessage(), this.idleWarningTime);
    }

    // Set the final session timeout
    this.timeout = setTimeout(() => this.handleSessionTimeout(), this.sessionTimeout);
  }

  private handleSessionTimeout() {
    this.timeoutDialog?.close();
    this.authService.logout();
  }

  public setUserType = (token: any) => {
    this._isInternUser = getToken(token.access_token).user_type === 'Intern';
    this.userName.set(getToken(token.access_token).name);
    this.session.next(getToken(token.access_token));
  };

  public get isInternUser() {
    return this._isInternUser;
  }

  public getOrgId() {
    const currentSession = this.session.value;
    return currentSession ? currentSession.org_id : undefined;
  }

  public getOrgName() {
    const currentSession = this.session.value;
    return currentSession ? currentSession.org_name : undefined;
  }

  public getUserType(): string | null {
    const currentSession = this.session.value;
    return currentSession?.user_type || null;
  }

  private showWarningMessage() {
    this.warningShown = true;
    const dialogRef = this.dialog.open(SessionWarningDialogComponent, {
      header: 'Automatisk utloggning',
      width: '600px',
    });

    if (!dialogRef) {
      return;
    }
    this.timeoutDialog = dialogRef;
    dialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((result: any) => {
        this.warningShown = false;

        if (result) {
          // If "OK" is clicked, reset the session
          this.warningShown = false;
        } else {
          this.authService.logout(true);
          // If the user closes the dialog or ignores it, let the timeout continue
          console.log('User did not interact with the session warning.');
        }
      });
  }

  private clearSessionTimers() {
    clearTimeout(this.timeout);
    clearTimeout(this.warningTimeout);
  }

  public getAnvandareOrganisationer() {
    const anvandareOrganisationer = this.session.getValue()?.anvandare_organisationer;
    if (anvandareOrganisationer) {
      return JSON.parse(anvandareOrganisationer);
    }
    return undefined;
  }

  public getUserId() {
    return this.session.getValue()?.sub ?? '';
  }

  public userLoggedInToSingleOrg() {
    const rawAnvandareOrganisationer = this.session.getValue()?.anvandare_organisationer;
    if (!rawAnvandareOrganisationer) {
      this._loggedInSingleOrg.next(false);
      return;
    }
    const anvandareOrganisationer: AnvandareOrganisation[] = JSON.parse(rawAnvandareOrganisationer);
    this.anvandarOrganisationer.set(anvandareOrganisationer);
    this._loggedInSingleOrg.next(this.session.getValue()?.org_id != '');
  }
}
